import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getFirestore } from 'firebase/firestore';
import { Box, Button, TextField, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 600,
  margin: '0 auto',
  marginTop: theme.spacing(4),
}));

const FormContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(3),
}));

/**
 * EmergencyAdminAccess Component
 * 
 * This component provides a direct way to access admin features by:
 * 1. Signing in the user
 * 2. Setting admin roles in localStorage (bypassing Firestore security rules)
 * 3. Redirecting to the admin panel
 */
const EmergencyAdminAccess = () => {
  const [email, setEmail] = useState('kihembowilb@gmail.com');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  // Function to handle emergency admin access
  const handleEmergencyAccess = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 1. Sign in the user
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 2. Set admin roles in localStorage
      localStorage.setItem('konzo_admin_override', 'true');
      localStorage.setItem('konzo_user_id', user.uid);
      localStorage.setItem('konzo_user_roles', JSON.stringify({
        admin: true,
        superadmin: true,
        artist: true,
        user: true
      }));

      // 3. Try to update Firestore (but don't fail if it doesn't work)
      try {
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || email.split('@')[0],
          roles: {
            admin: true,
            superadmin: true,
            artist: true,
            user: true
          },
          isAdmin: true,
          updatedAt: new Date()
        }, { merge: true });
      } catch (firestoreError) {
        console.warn('Could not update Firestore, but continuing with localStorage override:', firestoreError);
      }

      // 4. Show success message
      setSuccess('Admin access granted! Redirecting to admin panel...');
      
      // 5. Redirect to admin panel after a short delay
      setTimeout(() => {
        navigate('/marketplace/admin/tools');
      }, 2000);
      
    } catch (error) {
      console.error('Error granting admin access:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to go back to marketplace
  const handleGoBack = () => {
    navigate('/marketplace');
  };

  return (
    <StyledPaper elevation={3}>
      <Typography variant="h4" gutterBottom>
        Emergency Admin Access
      </Typography>
      
      <Typography variant="body1" paragraph>
        This tool provides emergency access to admin features by bypassing the normal security checks.
        Use this only if you're having issues with the regular admin access.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <FormContainer component="form" onSubmit={handleEmergencyAccess}>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
        />
        
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
        />

        <ButtonContainer>
          <Button 
            variant="outlined" 
            onClick={handleGoBack}
            disabled={loading}
          >
            Go Back
          </Button>
          
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Grant Admin Access'}
          </Button>
        </ButtonContainer>
      </FormContainer>

      <Box mt={4}>
        <Typography variant="subtitle2" color="textSecondary">
          How this works: This tool sets admin roles in your browser's localStorage, 
          which allows you to bypass the Firestore security rules that are preventing access.
        </Typography>
      </Box>
    </StyledPaper>
  );
};

export default EmergencyAdminAccess;
